/*
 *   File : listing.js
 *   Author : https://evoqins.com
 *   Description : Store listing page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Custom component 
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSearchInput, CustomSelectBox } from '../../../Component/FormElements';
import { APIService } from '../../../Service';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { Loader } from '../../../Component/Others';
import { DownloadButton, PrimaryButton, TextButton } from '../../../Component/Buttons';
import { toast } from 'react-toastify';
import { StatisticCard } from '../../../Component/Cards';

const ITEMS_PER_PAGE = 20;

const STORE_SORT_FIELDS = [
    'store_id',
    'name',
    'location',
    'sales_specialist',
    'sales_unit',
];

const StoreListing = () => {

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const navigate = useNavigate();
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [searchValue, setSearchValue] = useState("");
    const [storeData, setStoreData] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [toggleFilter, setToggleFilter] = useState(false);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [selected, setSelected] = useState(null);

    const [summaryLoader, setSummaryLoader] = useState(true);
    const [summaryData, setSummaryData] = useState({});

    const [filterLoader, setFilterLoader] = useState(true);
    const [filterData, setFilterData] = useState([]);

    const [clearFilter, setClearFilter] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });

        _getStoreSummary();
        _getFilterData();
    }, []);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getStoreList();
    }, [currentPage, searchValue, clearFilter]);

    // handle search store 
    const _handleSearchStore = (value) => {
        setSearchValue(value);
        setLoading(true);
        setCurrentPage(1);
    };

    // handle paginate store list 
    const _handlePageChange = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !STORE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getStoreList(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // handle navigate store detail 
    const _handleNavigateDetail = (store_id) => {
        navigate('detail', {
            state: {
                store_id: store_id,
                tabIndex: 1
            }
        });
    };

    const _handleClearAll = () => {
        setClearFilter(true);
        setLoading(true);
        setCurrentPage(1);
        setSortField(null);
        setSortDirection(null);

        setSelected(filterData[0]);
    };

    // store list table 
    const TABLE_COLUMNS = [
        {
            name: 'Store',
            id: 'name',
            sortField: 'name',
            selector: row => row.name ? row.name : '-',
            cell: row => <>{row.name}</>,
            width: "260px",
            sortable: true
        },
        {
            name: 'Location',
            id: 'location',
            sortField: 'location',
            selector: row => row.location ? row.location : '-',
            sortable: true
        },
        {
            name: 'Store ID',
            id: 'store_id',
            sortField: 'store_id',
            selector: row => row.store_id === null ? '-' : row.store_id,
            width: "120px",
            sortable: true
        },
        // {
        //     name: 'Phone no.',
        //     selector: row => row.phone === null ? '-' : row.phone,
        //     width: "160px"
        // },
        {
            name: 'Sales specialists',
            id: 'sales_specialist',
            sortField: 'sales_specialist',
            selector: row => row.sales_specialist,
            width: "150px",
            sortable: true
        },
        {
            name: PROFILE_DATA.country === "Egypt" ? 'Sales (in units)' : 'Sales (in ltrs)',
            id: 'sales_unit',
            sortField: 'sales_unit',
            selector: row => row.sales_unit,
            sortable: true,
            width: "150px",
        },
        // {
        //     name: 'Revenue generated',
        //     selector: row => row.revenue,
        //     width: "180px"
        // }
    ];

    // API - Get all stores
    const _getStoreList = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        let is_post = true;
        let url = 'CA/store/list';
        let data = {
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "query": searchValue,
            "city_id": selected?.value || null,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setStoreData(response.data);
            } else {
                setStoreData({});
            }
            setLoading(false);
            setClearFilter(false);
        });
    };

    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "CA/store/download";
        let data = {
            "query": searchValue,
            "city_id": selected?.value || null,
            "sort_by": sortField ? [sortField] : null,
            "order": sortDirection ? [sortDirection] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    // API - Download data
    const _getStoreSummary = () => {
        setSummaryLoader(true);

        let is_post = false;
        let url = "CA/store/metrics";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response?.status_code === 200) {
                setSummaryData(response?.data ?? {});
            } else {
                setSummaryData({});
            }
            setSummaryLoader(false);
        });
    };

    // API - Download data
    const _getFilterData = () => {
        setFilterLoader(true);

        let is_post = false;
        let url = "CA/store/filter-data";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                let updated_data = response.data.city.map((data) => ({
                    value: data.id,
                    label: data.name
                }));

                const updated_filter_data = [{ value: null, label: "All" }, ...updated_data];

                setFilterData(updated_filter_data);
                setSelected(updated_filter_data[0]);

            } else {
                setFilterData([]);
            }
            setFilterLoader(false);
        });
    };

    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                    All Stores
                </h1>
            </div>

            {/* Stores */}
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='row g-3 row-gap-3 row-cols-lg-4 row-cols-md-4 row-cols-sm-2 row-cols-2'>
                    <div className='col m-0'>
                        <StatisticCard
                            loader={summaryLoader}
                            icon={require("../../../Assets/Image/CountryAdmin/total-stores.svg").default}
                            quantity={summaryData.total_stores}
                            title={"Total stores"}
                            className="e-border-radius-8 p-3" />
                    </div>
                    <div className='col m-0 '>
                        <StatisticCard
                            loader={summaryLoader}
                            icon={require("../../../Assets/Image/CountryAdmin/location.svg").default}
                            quantity={summaryData.location_covered}
                            title={"Areas covered"}
                            className="e-border-radius-8 p-3" />
                    </div>
                    <div className='col m-0 '>
                        <StatisticCard
                            loader={summaryLoader}
                            icon={require("../../../Assets/Image/CountryAdmin/sales-specialists.svg").default}
                            quantity={summaryData.sales_specialists}
                            title={"Active sales specialists"}
                            className="e-border-radius-8 p-3" />
                    </div>
                    <div className='col m-0 '>
                        <StatisticCard
                            loader={summaryLoader}
                            icon={require("../../../Assets/Image/CountryAdmin/sales.svg").default}
                            quantity={summaryData.sales_liters}
                            title={PROFILE_DATA.country === "Egypt" ? "Sales (Units)" : "Volume (Ltr)"}
                            className="e-border-radius-8 p-3" />
                    </div>
                </div>

                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16  e-border-1px-platinum-dark mt-4'>
                    <div className=' px-4'>
                        <div className='row'>
                            <div className='col-lg-4 col-md-5 col-sm-4'>
                                <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px' >
                                    Overview
                                </h4>
                            </div>
                            <div className='col-lg-8 col-md-7 col-sm-8 pt-xl-0 pt-lg-0 pt-md-0 pt-sm-0 pt-2 text-end'>
                                <div className='d-flex justify-content-end align-items-center gap-3'>
                                    <DownloadButton loader={downloadLoader}
                                        onClick={_download} />

                                    <div className='position-relative'>
                                        <CustomSearchInput placeholder="Search by store name & id"
                                            type="text"
                                            require
                                            value={searchValue}
                                            className="w-100 py-2"
                                            iconClass="top-9px"
                                            labelStyle="e-font-weight-500"
                                            handleChange={(e) => _handleSearchStore(e)}
                                            handleClear={() => {
                                                setSearchValue("");
                                                setLoading(true);
                                            }} />
                                    </div>

                                    <div>
                                        <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                            draggable={false}
                                            height={40}
                                            width={40}
                                            className={` ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                            alt='filter'
                                            onClick={() => setToggleFilter(!toggleFilter)} />
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-3'>
                            <div className='row'>
                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Location"
                                        value={selected}
                                        options={filterData}
                                        loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setSelected(value)}
                                    />
                                </div>

                                <div className='col-md-3 col-sm-4 col-6 mt-auto'>
                                    <div className='d-flex gap-3 align-items-center '>
                                        <TextButton name="Clear all"
                                            onPress={_handleClearAll} />
                                        <PrimaryButton name="Filter now"
                                            className="px-3 py-1 e-border-radius-8"
                                            onPress={() => {
                                                setLoading(true);
                                                _getStoreList(1);
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {loading ?
                        // loader 
                        <Loader />
                        :
                        <>
                            {/* Store detail data table */}
                            {Object.keys(storeData).length !== 0 && storeData?.data?.length !== 0 ?
                                <div className='e-table e-store-table'>
                                    <DataTableComponent
                                        key={`store-table-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        pagination={true}
                                        data={storeData.data}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationServer={true}
                                        paginationTotalRows={storeData.total_count}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange}
                                        rowClick={(row) => _handleNavigateDetail(row.store_id)} />
                                </div>
                                :
                                // empty screen
                                <div className='h-100vh pt-5 mt-5'>
                                    <EmptyScreen message="No stores found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        </section>
    )
}

export default StoreListing;
